import React, { useState } from "react";
import { BarChart3 } from "lucide-react";
import { cn } from "@/lib/utils";
import { Segmented } from "@/components/vb";
import { SeverityBadge } from "./SeverityBadge";
import { SignalExposurePanel } from "./SignalExposurePanel";
import { CompetitorSignalCompare } from "./CompetitorSignalCompare";
import type { VerdictModel } from "./verdictModel";

/** Verdict hero (VB-F1). Shows the deterministic headline + severity + AI
 *  Trust Signal exposure by default. A small segmented toggle in the card's top-right
 *  switches the SAME card between the verdict and the focus-vs-competitors
 *  comparison, so the comparison gets the full card it reads best in. No
 *  MarcusAI commentary - that's Capability 3. */
export function VerdictHeader({ model }: { model: VerdictModel }) {
  const { overall, exposure } = model;
  const [showCompare, setShowCompare] = useState(false);
  const hasCompare = model.competitorExposure.length >= 2;
  const comparing = showCompare && hasCompare;

  return (
    <section className={cn("vb-verdict", comparing && "vb-verdict--compare")}>
      {hasCompare && (
        <div className="vb-verdict__toolbar">
          <Segmented
            ariaLabel="Verdict view"
            size="sm"
            value={comparing ? "compare" : "verdict"}
            onChange={(v) => setShowCompare(v === "compare")}
            options={[
              { value: "verdict", label: "Verdict" },
              { value: "compare", label: "vs. Competitors", icon: <BarChart3 size={12} /> },
            ]}
          />
        </div>
      )}

      <div className="vb-verdict__body">
        {comparing ? (
          <CompetitorSignalCompare key="compare" rows={model.competitorExposure} />
        ) : (
          <React.Fragment key="verdict">
            <div className="vb-verdict__main">
              <div className="vb-verdict__eyebrow">Buyer verdict</div>
              <h2 className="vb-verdict__headline">
                {overall.headline}
                <SeverityBadge severity={overall.severity} />
              </h2>
              <p className="vb-verdict__detail">{overall.detail}</p>
            </div>
            <SignalExposurePanel exposure={exposure} />
          </React.Fragment>
        )}
      </div>

      {!comparing && <ScoringGuide companyName={model.companyName} total={exposure.total} />}
    </section>
  );
}

/** Always-visible "how to read this" key. Spells out what Strong / Mixed / Weak
 *  mean and—critically—that the AI Trust Signal count is breadth of coverage, not
 *  strength, so a high "surfaced" number isn't mistaken for a good result. */
function ScoringGuide({ companyName, total }: { companyName: string; total: number }) {
  return (
    <div className="vb-scoreguide">
      <div className="vb-scoreguide__row">
        <span className="vb-scoreguide__lbl">How to read this</span>
        <ul className="vb-scoreguide__keys">
          <li>
            <SeverityBadge severity="strong" /> AI consistently names {companyName}
          </li>
          <li>
            <SeverityBadge severity="mid" /> {companyName} shows up, but rivals do too
          </li>
          <li>
            <SeverityBadge severity="weak" /> AI rarely names {companyName}
          </li>
        </ul>
      </div>
      <p className="vb-scoreguide__note">
        Everything here revolves around the {total} AI Trust Signals: the count shows how many came up
        across the answers, not how strong each one is. A buyer will run into these signals sooner
        or later, so what matters is understanding how they work.
      </p>
    </div>
  );
}

export default VerdictHeader;
